import {
  ALLOWED_NATIVE_EXTENSIONS,
  ALLOWED_PDF_EXTENSIONS,
  DOCUMENT_INDICES,
  DOCUMENT_NATURE_OPTIONS,
  MAX_NATIVE_SIZE_BYTES,
  MAX_PDF_SIZE_BYTES,
} from "@etudescarre/constants";
import { Button, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { todayLocal } from "@/lib/utils/date";
import { useUnsavedChangesGuard } from "@/lib/utils/unsaved-changes";
import { useYardContext } from "@/providers/yard-context";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Intervention = {
  id: number;
  zoneName: string;
  zoneType: string | null;
  buildingName: string | null;
  performanceName: string;
};

type DocumentFormData = {
  number: string;
  name: string;
  nature: string;
  index: string;
  dateDocument: string;
  interventionIds: number[];
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// A proposed document is never a maquette: those go through their own form.
const NATURE_OPTIONS = DOCUMENT_NATURE_OPTIONS.filter(
  (option) => option.value !== "maquette",
);

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ProposeDocumentsPanelProps = {
  onClose: () => void;
};

export function ProposeDocumentsPanel({ onClose }: ProposeDocumentsPanelProps) {
  const { yard, contracts } = useYardContext();

  // Only travaux rang 2 contracts held by the current user can propose docs.
  const rang2Contracts = contracts.filter((c) => c.type === "travaux_n_2");

  const [contractId, setContractId] = useState<number | null>(
    rang2Contracts.length === 1 ? rang2Contracts[0].id : null,
  );
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [loadingInterventions, setLoadingInterventions] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [nativeFile, setNativeFile] = useState<File | null>(null);
  const [proposed, setProposed] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const documentForm = useForm<DocumentFormData>({
    defaultValues: {
      number: "",
      name: "",
      nature: "",
      index: "O",
      dateDocument: todayLocal(),
      interventionIds: [],
    },
  });

  const number = documentForm.watch("number");
  const name = documentForm.watch("name");
  const selectedNature = documentForm.watch("nature");
  const selectedIndex = documentForm.watch("index");
  const selectedInterventions = documentForm.watch("interventionIds");

  // Warn before losing the in-progress proposal draft (refresh, nav, etc.).
  const isDirty =
    number.trim().length > 0 ||
    name.trim().length > 0 ||
    selectedNature.length > 0 ||
    selectedInterventions.length > 0 ||
    isNotNil(pdfFile) ||
    isNotNil(nativeFile);
  useUnsavedChangesGuard(isDirty);

  const contractOptions = rang2Contracts.map((c) => ({
    label: `${c.performerCompanyName} (${c.code ?? c.type})`,
    value: String(c.id),
  }));

  const fetchInterventions = useCallback(
    async (id: number) => {
      setLoadingInterventions(true);
      try {
        const { data } = await apiClient.GET(
          "/api/yards/{yardId}/receptions/interventions",
          {
            params: {
              path: { yardId: String(yard.id) },
              query: { contractId: String(id) },
            },
          },
        );
        if (isNotNil(data)) {
          setInterventions(data.data.interventions);
        }
      } catch {
        // ignore
      } finally {
        setLoadingInterventions(false);
      }
    },
    [yard.id],
  );

  useEffect(() => {
    if (isNotNil(contractId)) {
      fetchInterventions(contractId);
    }
  }, [contractId, fetchInterventions]);

  const handleSubmit = documentForm.handleSubmit(async (data) => {
    if (isNil(contractId)) {
      toaster.error({
        title: "Erreur",
        description: "Sélectionnez un contrat",
      });
      return;
    }
    if (data.interventionIds.length === 0) {
      toaster.error({
        title: "Erreur",
        description: "Sélectionnez au moins une intervention",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const metadata = {
        performanceId: 1,
        number: data.number,
        dateDocument: new Date(data.dateDocument).toISOString(),
        name: data.name,
        ...(data.nature ? { nature: data.nature } : {}),
        index: data.index || "O",
        interventionIds: data.interventionIds,
      };

      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/receptions/{receptionId}/documents",
        {
          params: {
            path: {
              yardId: String(yard.id),
              // The rang 2 contract is its own emetteur ("reception") token.
              receptionId: String(contractId),
            },
          },
          body: {
            metadata: JSON.stringify(metadata),
            pdf: pdfFile ?? undefined,
            native: nativeFile ?? undefined,
          },
          bodySerializer(body) {
            const fd = new FormData();
            for (const [key, value] of Object.entries(
              body as Record<string, unknown>,
            )) {
              if (value instanceof File) {
                fd.append(key, value, value.name);
              } else if (isNotNil(value)) {
                fd.append(key, String(value));
              }
            }
            return fd;
          },
        },
      );

      if (error) {
        toaster.error({
          title: "Erreur",
          description:
            error.error ??
            "La proposition n'a pas pu être enregistrée, réessayez",
        });
        return;
      }

      setProposed((prev) => prev + 1);
      toaster.success({
        title: "Succès",
        description: "Document ajouté à la proposition",
      });

      documentForm.reset();
      setPdfFile(null);
      setNativeFile(null);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (isNil(file)) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (
      isNil(ext) ||
      !ALLOWED_PDF_EXTENSIONS.includes(
        ext as (typeof ALLOWED_PDF_EXTENSIONS)[number],
      )
    ) {
      toaster.error({
        title: "Erreur",
        description: "Le fichier doit être au format PDF",
      });
      e.target.value = "";
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      toaster.error({
        title: "Erreur",
        description: "Le fichier PDF dépasse 50 Mo",
      });
      e.target.value = "";
      return;
    }
    setPdfFile(file);
  };

  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (isNil(file)) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (
      isNil(ext) ||
      !ALLOWED_NATIVE_EXTENSIONS.includes(
        ext as (typeof ALLOWED_NATIVE_EXTENSIONS)[number],
      )
    ) {
      toaster.error({
        title: "Erreur",
        description: `Extension non autorisée. Formats acceptés : ${ALLOWED_NATIVE_EXTENSIONS.join(", ")}`,
      });
      e.target.value = "";
      return;
    }
    if (file.size > MAX_NATIVE_SIZE_BYTES) {
      toaster.error({
        title: "Erreur",
        description: "Le fichier natif dépasse 200 Mo",
      });
      e.target.value = "";
      return;
    }
    setNativeFile(file);
  };

  const toggleIntervention = (id: number) => {
    if (selectedInterventions.includes(id)) {
      documentForm.setValue(
        "interventionIds",
        selectedInterventions.filter((i) => i !== id),
      );
    } else {
      documentForm.setValue("interventionIds", [...selectedInterventions, id]);
    }
  };

  const toggleAllInterventions = () => {
    if (selectedInterventions.length === interventions.length) {
      documentForm.setValue("interventionIds", []);
    } else {
      documentForm.setValue(
        "interventionIds",
        interventions.map((i) => i.id),
      );
    }
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
      >
        Retour aux actions
      </button>

      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        Proposer des documents d'exécution
      </h3>

      {rang2Contracts.length === 0 ? (
        <p className="text-sm text-zinc-500">
          Aucun contrat travaux rang 2 ne vous permet de proposer des documents.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {proposed > 0 && (
            <div className="rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
              {proposed} document(s) ajouté(s) à la proposition
            </div>
          )}

          <div>
            <span className={labelClass}>
              Contrat rang 2 <span className="text-red-500">*</span>
            </span>
            <Select
              options={contractOptions}
              value={isNotNil(contractId) ? [String(contractId)] : []}
              onValueChange={(details) => {
                const val = details.value[0];
                setContractId(val ? Number(val) : null);
              }}
              placeholder="Sélectionnez votre contrat"
            />
          </div>

          <div>
            <label htmlFor="proposeNumber" className={labelClass}>
              N° du document <span className="text-red-500">*</span>
            </label>
            <input
              id="proposeNumber"
              type="text"
              {...documentForm.register("number")}
              required
              minLength={1}
              maxLength={255}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="proposeName" className={labelClass}>
              Intitulé <span className="text-red-500">*</span>
            </label>
            <input
              id="proposeName"
              type="text"
              {...documentForm.register("name")}
              required
              minLength={1}
              maxLength={255}
              className={inputClass}
            />
          </div>

          <div>
            <span className={labelClass}>Nature</span>
            <Select
              options={NATURE_OPTIONS}
              value={selectedNature ? [selectedNature] : []}
              onValueChange={(details) => {
                documentForm.setValue("nature", details.value[0] ?? "");
              }}
              placeholder="Sélectionnez une nature"
            />
          </div>

          <div>
            <span className={labelClass}>Indice</span>
            <Select
              options={DOCUMENT_INDICES.map((idx) => ({
                label: idx,
                value: idx,
              }))}
              value={[selectedIndex]}
              onValueChange={(details) => {
                documentForm.setValue("index", details.value[0] ?? "O");
              }}
              placeholder="Indice"
            />
          </div>

          <div>
            <label htmlFor="proposeDate" className={labelClass}>
              Date du document <span className="text-red-500">*</span>
            </label>
            <input
              id="proposeDate"
              type="date"
              {...documentForm.register("dateDocument")}
              required
              className={inputClass}
            />
          </div>

          <div>
            <span className={labelClass}>
              Niveaux et façades <span className="text-red-500">*</span>
            </span>
            {loadingInterventions ? (
              <p className="text-sm text-zinc-500">Chargement...</p>
            ) : interventions.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Aucune intervention trouvée pour ce contrat
              </p>
            ) : (
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-zinc-300 p-2 dark:border-zinc-700">
                <label className="flex items-center gap-2 border-b border-zinc-200 pb-1 text-sm font-medium dark:border-zinc-700">
                  <input
                    type="checkbox"
                    checked={
                      selectedInterventions.length === interventions.length
                    }
                    onChange={toggleAllInterventions}
                  />
                  Tout sélectionner
                </label>
                {interventions.map((intv) => (
                  <label
                    key={intv.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedInterventions.includes(intv.id)}
                      onChange={() => toggleIntervention(intv.id)}
                    />
                    <span>
                      {intv.buildingName ? `${intv.buildingName} - ` : ""}
                      {intv.zoneName} / {intv.performanceName}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="proposePdf" className={labelClass}>
              Fichier PDF
            </label>
            <input
              id="proposePdf"
              type="file"
              accept=".pdf"
              onChange={handlePdfChange}
              className="w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400"
            />
            {isNotNil(pdfFile) && (
              <p className="mt-1 text-xs text-zinc-500">
                {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(1)} Mo)
              </p>
            )}
          </div>

          <div>
            <label htmlFor="proposeNative" className={labelClass}>
              Fichier natif (CAD)
            </label>
            <input
              id="proposeNative"
              type="file"
              accept={ALLOWED_NATIVE_EXTENSIONS.map((e) => `.${e}`).join(",")}
              onChange={handleNativeChange}
              className="w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400"
            />
            {isNotNil(nativeFile) && (
              <p className="mt-1 text-xs text-zinc-500">
                {nativeFile.name} ({(nativeFile.size / 1024 / 1024).toFixed(1)}{" "}
                Mo)
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              outline
              onClick={onClose}
              disabled={isSubmitting}
            >
              Terminer
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "En cours..." : "Ajouter à la proposition"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
